import { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useMeta } from "@/context/MetaContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import DeleteCustomersDialog from "@/components/DeleteCustomersDialog";
import { stageStyle, responseStyle, LANG_LABEL, fmtDate, relTime } from "@/lib/pgt";
import CopilotPanel from "@/components/copilot/CopilotPanel";
import TimelinePanel from "@/components/copilot/TimelinePanel";
import DetailsPanel from "@/components/copilot/DetailsPanel";
import ResponseDialog from "@/components/copilot/ResponseDialog";
import ArchiveDialog from "@/components/copilot/ArchiveDialog";
import EditCustomerDialog from "@/components/EditCustomerDialog";
import {
  ArrowLeft, Loader2, Phone, Building2, User, MessageCircleReply, Archive,
  RotateCcw, AlertTriangle, CalendarClock, Clock, Pencil, Trash2,
} from "lucide-react";
import { toast } from "sonner";

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectProduct = searchParams.get("product");
  const autoDraft = searchParams.get("draft") === "1";
  const initialTab = searchParams.get("tab") || "copilot";
  const qc = useQueryClient();
  const { meta } = useMeta();
  const [tab, setTab] = useState(initialTab);
  const [respOpen, setRespOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  const { data: customer, isLoading } = useQuery({
    queryKey: ["customer", id],
    queryFn: async () => (await api.get(`/customers/${id}`)).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["customer", id] });
    qc.invalidateQueries({ queryKey: ["timeline", id] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["customers"] });
  };

  const reactivate = async () => {
    await api.post(`/customers/${id}/reactivate`);
    toast.success("Customer reactivated");
    refresh();
  };

  if (isLoading || !customer)
    return <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>;

  const c = customer;

  return (
    <div className="animate-fade-up">
      <button onClick={() => navigate(-1)} className="mb-3 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-forest" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Context header */}
      <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-5" data-testid="customer-profile-header">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-xl sm:text-2xl font-bold text-stone-900">{c.name}</h1>
              <Badge variant="outline" className={`${stageStyle(c.stage)} text-[11px]`}>{c.stage_label}</Badge>
              <Badge variant="outline" className="text-[11px] capitalize">{c.customer_type}</Badge>
              {c.status !== "active" && <Badge variant="outline" className="text-[11px] bg-stone-100 text-stone-600 capitalize">{c.status}</Badge>}
              {c.overdue && <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"><AlertTriangle className="h-3.5 w-3.5" /> Overdue</span>}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-stone-500">
              <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {c.mobile}</span>
              {c.company && <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {c.company}</span>}
              <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" /> {c.assigned_name}</span>
              <span>Lang: {LANG_LABEL[c.preferred_language]}</span>
            </div>
            {c.sub_stage && <div className="mt-1.5 text-xs text-stone-400">Sub-stage: {c.sub_stage}</div>}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-customer-button">
              <Pencil className="mr-1.5 h-4 w-4" /> Edit
            </Button>
            <Button variant="outline" onClick={() => setRespOpen(true)} data-testid="record-response-button">
              <MessageCircleReply className="mr-1.5 h-4 w-4" /> Record response
            </Button>
            {c.status === "active" ? (
              <Button variant="outline" className="text-stone-600" onClick={() => setArchiveOpen(true)} data-testid="archive-button">
                <Archive className="mr-1.5 h-4 w-4" /> Archive
              </Button>
            ) : (
              <Button variant="outline" onClick={reactivate} data-testid="reactivate-button">
                <RotateCcw className="mr-1.5 h-4 w-4" /> Reactivate
              </Button>
            )}
            <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" onClick={() => setDelOpen(true)} data-testid="delete-customer-button">
              <Trash2 className="mr-1.5 h-4 w-4" /> Delete
            </Button>
          </div>
        </div>

        {/* Metrics strip */}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Metric icon={Clock} label="Days since contact" value={c.days_since_last != null ? `${c.days_since_last}` : "—"} />
          <Metric icon={MessageCircleReply} label="Unanswered follow-ups" value={c.unanswered_followups} tone={c.unanswered_followups >= 3 ? "text-red-600" : ""} />
          <Metric icon={CalendarClock} label="Next follow-up" value={fmtDate(c.next_follow_up_date)} />
          <Metric icon={AlertTriangle} label="Status" value={c.overdue ? "Overdue" : "On track"} tone={c.overdue ? "text-red-600" : "text-forest"} />
        </div>

        {/* Last comms */}
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-forest/15 bg-whatsapp-bg/30 p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">Last message sent {c.last_message_at ? `· ${relTime(c.last_message_at)}` : ""}</div>
            <p className="mt-1 text-sm text-stone-700 line-clamp-3">{c.last_message || "No message sent yet"}</p>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">Last customer response {c.last_response_at ? `· ${relTime(c.last_response_at)}` : ""}</div>
              {c.last_response_status && <span className={`rounded-full border px-1.5 py-0.5 text-[10px] ${responseStyle(c.last_response_status)}`}>{c.last_response_status}</span>}
            </div>
            <p className="mt-1 text-sm text-stone-700 line-clamp-3">{c.last_response || "No response recorded yet"}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="mt-5">
        <TabsList data-testid="profile-tabs">
          <TabsTrigger value="copilot" data-testid="tab-copilot">Communication Copilot</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
        </TabsList>
        <TabsContent value="copilot" className="mt-4">
          <CopilotPanel customer={c} onChanged={refresh} initialProductId={preselectProduct} initialAutoGenerate={autoDraft} />
        </TabsContent>
        <TabsContent value="timeline" className="mt-4">
          <TimelinePanel customerId={c.id} />
        </TabsContent>
        <TabsContent value="details" className="mt-4">
          <DetailsPanel customer={c} onChanged={refresh} />
        </TabsContent>
      </Tabs>

      <ResponseDialog open={respOpen} onOpenChange={setRespOpen} customer={c} onDone={refresh} />
      <ArchiveDialog open={archiveOpen} onOpenChange={setArchiveOpen} customer={c} onDone={refresh} />
      <EditCustomerDialog open={editOpen} onOpenChange={setEditOpen} customer={c} meta={meta} onSaved={() => { setEditOpen(false); refresh(); }} />

      {delOpen && (
        <DeleteCustomersDialog
          open
          customers={[c]}
          onOpenChange={setDelOpen}
          onDeleted={() => { qc.removeQueries({ queryKey: ["customer", id] }); navigate("/customers"); }}
        />
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50/60 p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-stone-500"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className={`mt-1 font-heading text-lg font-bold text-stone-900 ${tone || ""}`}>{value}</div>
    </div>
  );
}
